import { useEffect } from 'react';
import { Tabs, Tab } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import usePermissions from 'src/hooks/usePermissions';
import TransactionsComponent from 'src/components/TransactionsComponent';
import SearchTransactionComponent from 'src/components/SearchTransactionComponent';

import { PortalTab, TAB_PARAM, TAB_CONSULTA, TAB_DESCARGA, resolveTab } from './utils';
import { styles } from './styles';

/**
 * Portal "Mis Pagos": shell de tabs Consulta/Descarga con visibilidad asimétrica por permisos (H1/H2).
 * - read:transactions habilita Consulta (búsqueda puntual exacta, H3).
 * - download:transactions habilita Descarga (listado + filtros + KPIs) y también Consulta.
 * - manage:transactions (MODO interno) habilita ambas tabs; el bypass del filtro por CUIT lo aplica el BFF.
 * La tab activa se persiste en la URL (?tab=).
 *
 * En Consulta la URL no lleva mas que la tab. Los filtros de Descarga persisten
 * mientras se esta en esa solapa; al pasar a Consulta se sacan de la direccion,
 * porque ahi la fecha de un pago buscado y el tope de un filtro se leen igual y
 * la busqueda puntual no debe dejar rastro de lo que el comercio consulto.
 *
 * Volver a Descarga la deja sin filtros: recordarlos pedia un estado paralelo a
 * la URL, y con el la solapa activa y la direccion podian discrepar.
 */
const MyPaymentsComponent = () => {
  const { classes } = styles();
  const { canRead, canDownload, canManage } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();

  const showConsulta = canRead || canDownload || canManage;
  const showDescarga = canDownload || canManage;

  const activeTab = resolveTab(searchParams.get(TAB_PARAM), { showConsulta, showDescarga });

  useEffect(() => {
    const tabInUrl = searchParams.get(TAB_PARAM);
    // Entrar directo con filtros y ?tab=consulta tambien tiene que limpiarlos.
    const carriesFilters =
      activeTab === TAB_CONSULTA && [...searchParams.keys()].some((key) => key !== TAB_PARAM);

    if (tabInUrl === activeTab && !carriesFilters) return;

    const next = carriesFilters ? new URLSearchParams() : new URLSearchParams(searchParams);
    next.set(TAB_PARAM, activeTab);
    setSearchParams(next, { replace: true });
  }, [activeTab, searchParams, setSearchParams]);

  const handleTabChange = (_event: React.SyntheticEvent, value: PortalTab) => {
    const next = value === TAB_CONSULTA ? new URLSearchParams() : new URLSearchParams(searchParams);
    next.set(TAB_PARAM, value);
    setSearchParams(next);
  };

  return (
    <div>
      <Tabs
        className={classes.tabs}
        value={activeTab}
        onChange={handleTabChange}
        aria-label="Mis Pagos"
        textColor="primary"
        indicatorColor="primary">
        {showConsulta && <Tab label="Consulta" value={TAB_CONSULTA} />}
        {showDescarga && <Tab label="Descarga" value={TAB_DESCARGA} />}
      </Tabs>

      {activeTab === TAB_CONSULTA && showConsulta && <SearchTransactionComponent />}

      {activeTab === TAB_DESCARGA && showDescarga && <TransactionsComponent />}
    </div>
  );
};

export default MyPaymentsComponent;
